import { CheckedState } from "@radix-ui/react-checkbox";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import {
  usePutActiveMasterDataPdfMutation,
  useShowActiveMasterDataPdfQuery,
} from "@/services/backend/company/api/master-data-pdf";

/**
 * MasterDataPdfActiveCheckbox marks the master data a company's
 * documents are rendered with.
 *
 * Ticking a row assigns it and unticks whichever row was assigned
 * before, because the backend keeps one assignment per company —
 * there is no second row to clear. Unticking clears the assignment.
 *
 * The assignment is read rather than the assigned master data:
 * having none assigned is a state the checkbox has to render, and a
 * query that answered it with an error would leave the previous
 * value in the cache and the box ticked.
 */
export function MasterDataPdfActiveCheckbox({
  companyId,
  masterDataPdfId,
}: {
  companyId: string;
  masterDataPdfId: string;
}) {
  const { data: active, error: showError } = useShowActiveMasterDataPdfQuery({
    companyId,
  });
  const [put, { isLoading, error: putError, reset }] =
    usePutActiveMasterDataPdfMutation();

  const { toast } = useToast();

  const error = showError ?? putError;
  if (error) {
    toast({ ...parseRTKQueryError(error), variant: "destructive" });
    reset();
  }

  const onCheckedChange = (state: CheckedState) => {
    if (isLoading || state === "indeterminate") {
      return;
    }
    put({ companyId, defaultId: state ? masterDataPdfId : null });
  };

  return (
    <Checkbox
      checked={active?.defaultId === masterDataPdfId}
      onCheckedChange={onCheckedChange}
      className={isLoading ? "animate-pulse" : ""}
      disabled={isLoading}
    />
  );
}
